import React, { useState, useEffect } from "react";
import {
    FaExclamationTriangle,
    FaBox,
    FaShoppingCart,
    FaStar,
    FaRupeeSign,
    FaSms,
    FaMobileAlt,
    FaEnvelope,
    FaCheckCircle,
    FaToggleOn,
    FaToggleOff,
    FaCog,
    FaBell,
    FaSave,
    FaUndo
} from "react-icons/fa";

function CustomAlerts({ isMobile }) {
    // State for alert configurations
    const [alerts, setAlerts] = useState({
        lowStock: {
            enabled: true,
            threshold: 10,
            notifyViaSms: true,
            notifyViaPush: true,
            notifyViaEmail: true,
            category: 'inventory',
            frequency: 'immediate',
            description: 'Get notified when inventory is running low'
        },
        highValueOrders: {
            enabled: true,
            threshold: 5000,
            notifyViaSms: true,
            notifyViaPush: true,
            notifyViaEmail: false,
            category: 'orders',
            frequency: 'immediate',
            description: 'Alerts for orders above specific value'
        },
        newOrders: {
            enabled: false,
            threshold: 0,
            notifyViaSms: false,
            notifyViaPush: true,
            notifyViaEmail: false,
            category: 'orders',
            frequency: 'immediate',
            description: 'Alerts for every new order received'
        },
        customerReviews: {
            enabled: true,
            ratingThreshold: 3,
            notifyViaSms: false,
            notifyViaPush: false,
            notifyViaEmail: true,
            category: 'reviews',
            frequency: 'daily',
            description: 'Alerts for critical customer reviews'
        },
        paymentReceived: {
            enabled: true,
            threshold: 0,
            notifyViaSms: true,
            notifyViaPush: true,
            notifyViaEmail: true,
            category: 'payments',
            frequency: 'immediate',
            description: 'Alerts for successful payments'
        },
        paymentFailed: {
            enabled: true,
            threshold: 0,
            notifyViaSms: true,
            notifyViaPush: true,
            notifyViaEmail: true,
            category: 'payments',
            frequency: 'immediate',
            description: 'Alerts for failed payment attempts'
        },
        systemMaintenance: {
            enabled: true,
            threshold: 0,
            notifyViaSms: false,
            notifyViaPush: true,
            notifyViaEmail: true,
            category: 'system',
            frequency: 'immediate',
            description: 'Alerts for system maintenance and updates'
        }
    });

    const [originalAlerts, setOriginalAlerts] = useState({});

    // Initialize original alerts on component mount
    useEffect(() => {
        setOriginalAlerts(JSON.parse(JSON.stringify(alerts)));
    }, []);

    // Toggle alert configuration
    const toggleAlert = (alertType) => {
        setAlerts(prev => ({
            ...prev,
            [alertType]: {
                ...prev[alertType],
                enabled: !prev[alertType].enabled
            }
        }));
    };

    // Update alert threshold
    const updateAlertThreshold = (alertType, value) => {
        setAlerts(prev => ({
            ...prev,
            [alertType]: {
                ...prev[alertType],
                threshold: Math.max(0, parseInt(value) || 0)
            }
        }));
    };

    // Update rating threshold
    const updateRatingThreshold = (alertType, value) => {
        setAlerts(prev => ({
            ...prev,
            [alertType]: {
                ...prev[alertType],
                ratingThreshold: Math.min(5, Math.max(1, parseFloat(value) || 3))
            }
        }));
    };

    // Toggle notification channel for alert
    const toggleAlertChannel = (alertType, channel) => {
        setAlerts(prev => ({
            ...prev,
            [alertType]: {
                ...prev[alertType],
                [channel]: !prev[alertType][channel]
            }
        }));
    };

    // Update alert frequency
    const updateAlertFrequency = (alertType, frequency) => {
        setAlerts(prev => ({
            ...prev,
            [alertType]: {
                ...prev[alertType],
                frequency: frequency
            }
        }));
    };

    // Save all alerts
    const saveAllAlerts = () => {
        // In a real app, this would make an API call
        setOriginalAlerts(JSON.parse(JSON.stringify(alerts)));
        alert('All alert configurations saved successfully!');
    };

    // Reset to original values
    const resetAlerts = () => {
        if (window.confirm('Are you sure you want to reset all changes?')) {
            setAlerts(JSON.parse(JSON.stringify(originalAlerts)));
        }
    };

    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
        return JSON.stringify(alerts) !== JSON.stringify(originalAlerts);
    };

    // Get alert icon
    const getAlertIcon = (type) => {
        switch (type) {
            case 'lowStock': return <FaBox />;
            case 'highValueOrders': return <FaRupeeSign />;
            case 'newOrders': return <FaShoppingCart />;
            case 'customerReviews': return <FaStar />;
            case 'paymentReceived': return <FaRupeeSign />;
            case 'paymentFailed': return <FaExclamationTriangle />;
            case 'systemMaintenance': return <FaCog />;
            default: return <FaBell />;
        }
    };

    // Get alert color
    const getAlertColor = (type) => {
        switch (type) {
            case 'lowStock': return '#10b981';
            case 'highValueOrders': return '#3b82f6';
            case 'newOrders': return '#8b5cf6';
            case 'customerReviews': return '#f59e0b';
            case 'paymentReceived': return '#10b981';
            case 'paymentFailed': return '#ef4444';
            case 'systemMaintenance': return '#6b7280';
            default: return '#6B46C1';
        }
    };

    // Alert configurations data
    const alertConfigs = [
        {
            key: 'lowStock',
            title: 'Low Stock Alert',
            icon: <FaBox />,
            color: '#10b981'
        },
        {
            key: 'highValueOrders',
            title: 'High-Value Orders',
            icon: <FaRupeeSign />,
            color: '#3b82f6'
        },
        {
            key: 'newOrders',
            title: 'New Orders',
            icon: <FaShoppingCart />,
            color: '#8b5cf6'
        },
        {
            key: 'customerReviews',
            title: 'Customer Reviews',
            icon: <FaStar />,
            color: '#f59e0b'
        },
        {
            key: 'paymentReceived',
            title: 'Payment Received',
            icon: <FaRupeeSign />,
            color: '#10b981'
        },
        {
            key: 'paymentFailed',
            title: 'Payment Failed',
            icon: <FaExclamationTriangle />,
            color: '#ef4444'
        },
        {
            key: 'systemMaintenance',
            title: 'System Maintenance',
            icon: <FaCog />,
            color: '#6b7280'
        }
    ];

    return (
        <div style={{ padding: isMobile ? '20px' : '30px' }}>
            {/* Header Section */}
            <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                marginBottom: '30px',
                paddingBottom: '20px',
                borderBottom: '1px solid #e0e0e0',
                gap: isMobile ? '15px' : '0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: '#f5f3ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6B46C1',
                        fontSize: '22px'
                    }}>
                        <FaExclamationTriangle />
                    </div>
                    <div>
                        <h2 style={{
                            margin: '0 0 5px 0',
                            fontSize: isMobile ? '20px' : '24px',
                            fontWeight: '600',
                            color: '#1a1a1a'
                        }}>
                            Custom Alert Configuration
                        </h2>
                        <p style={{
                            margin: 0,
                            fontSize: isMobile ? '14px' : '16px',
                            color: '#666'
                        }}>
                            Configure alerts for low stock, high-value orders, and more
                        </p>
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap'
                }}>
                    {hasUnsavedChanges() && (
                        <button
                            onClick={resetAlerts}
                            style={{
                                backgroundColor: 'transparent',
                                color: '#6b7280',
                                border: '1px solid #d1d5db',
                                padding: '10px 20px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                                e.currentTarget.style.borderColor = '#6b7280';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                        >
                            <FaUndo /> Reset Changes
                        </button>
                    )}

                    <button
                        onClick={saveAllAlerts}
                        style={{
                            backgroundColor: hasUnsavedChanges() ? '#6B46C1' : '#9ca3af',
                            color: 'white',
                            border: 'none',
                            padding: '10px 24px',
                            borderRadius: '8px',
                            cursor: hasUnsavedChanges() ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            if (hasUnsavedChanges()) {
                                e.currentTarget.style.backgroundColor = '#5a3d9c';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (hasUnsavedChanges()) {
                                e.currentTarget.style.backgroundColor = '#6B46C1';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }
                        }}
                    >
                        <FaSave /> Save All Changes
                    </button>
                </div>
            </div>

            {/* Alert Configuration Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '20px',
                marginBottom: '40px'
            }}>
                {alertConfigs.map(config => {
                    const alert = alerts[config.key];
                    
                    return (
                        <div key={config.key} style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '25px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                            e.currentTarget.style.transform = 'translateY(-4px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                        >
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                marginBottom: '20px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px', flex: 1 }}>
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '10px',
                                        backgroundColor: alert.enabled ? config.color + '20' : '#f3f4f6',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: alert.enabled ? config.color : '#9ca3af',
                                        fontSize: '20px',
                                        flexShrink: 0
                                    }}>
                                        {config.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{
                                            margin: '0 0 5px 0',
                                            color: alert.enabled ? '#1a1a1a' : '#9ca3af',
                                            fontSize: '18px',
                                            fontWeight: '600'
                                        }}>
                                            {config.title}
                                        </h4>
                                        <p style={{
                                            margin: 0,
                                            color: alert.enabled ? '#6b7280' : '#d1d5db',
                                            fontSize: '14px',
                                            lineHeight: 1.5
                                        }}>
                                            {alert.description}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => toggleAlert(config.key)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '36px',
                                        color: alert.enabled ? config.color : '#d1d5db',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexShrink: 0,
                                        padding: 0,
                                        marginLeft: '10px'
                                    }}
                                >
                                    {alert.enabled ? <FaToggleOn /> : <FaToggleOff />}
                                </button>
                            </div>

                            {alert.enabled && (
                                <div style={{ marginTop: '20px' }}>
                                    {/* Threshold Settings */}
                                    {(config.key === 'lowStock' || config.key === 'highValueOrders') && (
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: '8px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151'
                                            }}>
                                                {config.key === 'lowStock' ? 'Alert when stock falls below:' : 'Alert for orders above:'}
                                            </label>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}>
                                                {config.key === 'highValueOrders' && (
                                                    <span style={{
                                                        fontSize: '16px',
                                                        fontWeight: 'bold',
                                                        color: config.color
                                                    }}>
                                                        ₹
                                                    </span>
                                                )}
                                                <input
                                                    type="number"
                                                    value={alert.threshold}
                                                    onChange={(e) => updateAlertThreshold(config.key, e.target.value)}
                                                    min={config.key === 'lowStock' ? "1" : "100"}
                                                    max={config.key === 'lowStock' ? "1000" : "1000000"}
                                                    step={config.key === 'lowStock' ? "1" : "100"}
                                                    style={{
                                                        flex: 1,
                                                        padding: '12px',
                                                        border: '2px solid #e5e7eb',
                                                        borderRadius: '8px',
                                                        fontSize: '16px',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = config.color}
                                                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                                />
                                                <span style={{
                                                    fontSize: '14px',
                                                    color: '#6b7280',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {config.key === 'lowStock' ? 'items' : ''}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Rating Threshold for Reviews */}
                                    {config.key === 'customerReviews' && (
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: '8px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151'
                                            }}>
                                                Alert for ratings below:
                                            </label>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}>
                                                <input
                                                    type="number"
                                                    value={alert.ratingThreshold}
                                                    onChange={(e) => updateRatingThreshold(config.key, e.target.value)}
                                                    min="1"
                                                    max="5"
                                                    step="0.5"
                                                    style={{
                                                        flex: 1,
                                                        padding: '12px',
                                                        border: '2px solid #e5e7eb',
                                                        borderRadius: '8px',
                                                        fontSize: '16px',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = config.color}
                                                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                                />
                                                <div style={{
                                                    display: 'flex',
                                                    gap: '4px'
                                                }}>
                                                    {[1, 2, 3, 4, 5].map(star => (
                                                        <FaStar 
                                                            key={star}
                                                            style={{
                                                                color: star <= alert.ratingThreshold ? config.color : '#e5e7eb',
                                                                fontSize: '18px'
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Notification Channels */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '10px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: '#374151'
                                        }}>
                                            Notification Channels:
                                        </label>
                                        <div style={{
                                            display: 'flex',
                                            gap: '10px',
                                            flexWrap: 'wrap'
                                        }}>
                                            {[
                                                { key: 'notifyViaSms', label: 'SMS', icon: <FaSms /> },
                                                { key: 'notifyViaPush', label: 'Push', icon: <FaMobileAlt /> },
                                                { key: 'notifyViaEmail', label: 'Email', icon: <FaEnvelope /> }
                                            ].map(channel => (
                                                <button
                                                    key={channel.key}
                                                    type="button"
                                                    onClick={() => toggleAlertChannel(config.key, channel.key)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        border: `2px solid ${alert[channel.key] ? config.color : '#e5e7eb'}`,
                                                        backgroundColor: alert[channel.key] ? config.color + '10' : 'white',
                                                        color: alert[channel.key] ? config.color : '#6b7280',
                                                        borderRadius: '20px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        fontSize: '13px',
                                                        fontWeight: '500',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!alert[channel.key]) {
                                                            e.currentTarget.style.borderColor = config.color;
                                                            e.currentTarget.style.color = config.color;
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!alert[channel.key]) {
                                                            e.currentTarget.style.borderColor = '#e5e7eb';
                                                            e.currentTarget.style.color = '#6b7280';
                                                        }
                                                    }}
                                                >
                                                    {channel.icon} {channel.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Frequency Settings */}
                                    {(config.key === 'newOrders' || config.key === 'customerReviews') && (
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: '10px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151'
                                            }}>
                                                Notification Frequency:
                                            </label>
                                            <div style={{
                                                display: 'flex',
                                                gap: '8px',
                                                flexWrap: 'wrap'
                                            }}>
                                                {['immediate', 'hourly', 'daily'].map(freq => (
                                                    <button
                                                        key={freq}
                                                        type="button"
                                                        onClick={() => updateAlertFrequency(config.key, freq)}
                                                        style={{
                                                            padding: '8px 16px',
                                                            border: `2px solid ${alert.frequency === freq ? config.color : '#e5e7eb'}`,
                                                            backgroundColor: alert.frequency === freq ? config.color + '10' : 'white',
                                                            color: alert.frequency === freq ? config.color : '#6b7280',
                                                            borderRadius: '20px',
                                                            cursor: 'pointer',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            textTransform: 'capitalize',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (alert.frequency !== freq) {
                                                                e.currentTarget.style.borderColor = config.color;
                                                                e.currentTarget.style.color = config.color;
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (alert.frequency !== freq) {
                                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                                                e.currentTarget.style.color = '#6b7280';
                                                            }
                                                        }}
                                                    >
                                                        {freq}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Status Summary */}
                                    <div style={{
                                        padding: '15px',
                                        backgroundColor: config.color + '10',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        color: config.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                    }}>
                                        <FaCheckCircle />
                                        <span>
                                            Alert active: {
                                                config.key === 'lowStock' 
                                                    ? `Notify when stock is below ${alert.threshold} items`
                                                    : config.key === 'highValueOrders'
                                                    ? `Notify for orders above ₹${alert.threshold.toLocaleString()}`
                                                    : config.key === 'customerReviews'
                                                    ? `Notify for reviews below ${alert.ratingThreshold} stars`
                                                    : 'Active and monitoring'
                                            }
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Summary Section */}
            <div style={{
                backgroundColor: '#f9fafb',
                padding: '25px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                marginTop: '20px'
            }}>
                <h3 style={{
                    margin: '0 0 20px 0',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1a1a1a'
                }}>
                    Alert Configuration Summary
                </h3>
                
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    gap: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '10px'
                        }}>
                            Active Alerts
                        </div>
                        <div style={{
                            fontSize: '32px',
                            fontWeight: '700',
                            color: '#10b981',
                            marginBottom: '5px'
                        }}>
                            {Object.values(alerts).filter(a => a.enabled).length}
                        </div>
                        <div style={{
                            fontSize: '14px',
                            color: '#6b7280'
                        }}>
                            out of {Object.keys(alerts).length} total alerts
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '10px'
                        }}>
                            SMS Alerts
                        </div>
                        <div style={{
                            fontSize: '32px',
                            fontWeight: '700',
                            color: '#3b82f6',
                            marginBottom: '5px'
                        }}>
                            {Object.values(alerts).filter(a => a.enabled && a.notifyViaSms).length}
                        </div>
                        <div style={{
                            fontSize: '14px',
                            color: '#6b7280'
                        }}>
                            configured for SMS notifications
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '10px'
                        }}>
                            Most Used Channel
                        </div>
                        <div style={{
                            fontSize: '32px',
                            fontWeight: '700',
                            color: '#8b5cf6',
                            marginBottom: '5px'
                        }}>
                            {(() => {
                                const channels = {
                                    sms: Object.values(alerts).filter(a => a.enabled && a.notifyViaSms).length,
                                    push: Object.values(alerts).filter(a => a.enabled && a.notifyViaPush).length,
                                    email: Object.values(alerts).filter(a => a.enabled && a.notifyViaEmail).length
                                };
                                const maxChannel = Object.keys(channels).reduce((a, b) => channels[a] > channels[b] ? a : b);
                                return maxChannel.charAt(0).toUpperCase() + maxChannel.slice(1);
                            })()}
                        </div>
                        <div style={{
                            fontSize: '14px',
                            color: '#6b7280'
                        }}>
                            most frequently selected channel
                        </div>
                    </div>
                </div>

                {/* Final Save Button */}
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <button
                        onClick={saveAllAlerts}
                        style={{
                            backgroundColor: hasUnsavedChanges() ? '#6B46C1' : '#9ca3af',
                            color: 'white',
                            border: 'none',
                            padding: '15px 40px',
                            borderRadius: '8px',
                            cursor: hasUnsavedChanges() ? 'pointer' : 'not-allowed',
                            fontSize: '16px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '12px',
                            boxShadow: hasUnsavedChanges() ? '0 4px 15px rgba(107, 70, 193, 0.3)' : 'none',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            if (hasUnsavedChanges()) {
                                e.currentTarget.style.backgroundColor = '#5a3d9c';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (hasUnsavedChanges()) {
                                e.currentTarget.style.backgroundColor = '#6B46C1';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }
                        }}
                    >
                        <FaSave /> Save All Alert Configurations
                    </button>
                    
                    {hasUnsavedChanges() && (
                        <p style={{
                            margin: '10px 0 0 0',
                            fontSize: '13px',
                            color: '#6b7280',
                            fontStyle: 'italic'
                        }}>
                            You have unsaved changes. Click to save.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CustomAlerts;